'use client';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useState } from 'react';
import { PlanDetail } from '@/app/components/shared/PlanDetail';
import { SubscriptionButton } from '@/app/components/shared/PlanDetail/components/SubscriptionButton';
import { toast } from '@/app/components/ui/use-toast';
import { useFetchProductList, useGotoManageStripe, usePayment, usePaymentIntent } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { PeriodType, SubscriptionProductType } from '@/app/types';
import { ComparisonTable } from './ComparisonTable';

const PricingContent: FC = () => {
  const { userInfo, isUserInfoInitialized, isSubscriptionInitialized, subscription, nextPlan } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    isSubscriptionInitialized: state.isSubscriptionInitialized,
    subscription: state.subscription,
    nextPlan: state.nextPlan,
  }));

  // 此处的 period 是为了 PlanDetail 和 ComparisonTable 的联动而设置的
  const [period, setPeriod] = useState<PeriodType | null>(null);
  const { getPaymentIntent, clearPaymentIntent } = usePaymentIntent();
  const { pay } = usePayment();
  const { loading: managementLoading, handleGotoManageStripe } = useGotoManageStripe();
  const intent = getPaymentIntent();
  const t = useTranslations();

  const {
    loading: loadingProducts,
    error,
    getProductsList,
    subscriptionProducts,
    subscriptionTabList,
    subscriptionInitialPeriod,
  } = useFetchProductList();

  useEffect(() => {
    // 用户信息加载完成 如果有userInfo则等订阅信息加载完成获取产品列表，如果没有userInfo则直接获取产品列表
    if (isUserInfoInitialized) {
      if (userInfo) {
        if (isSubscriptionInitialized) {
          getProductsList();
        }
      } else {
        getProductsList();
      }
    }
  }, [isUserInfoInitialized, isSubscriptionInitialized, userInfo]);

  // 渲染订阅按钮的函数
  const renderSubscriptionButton = (product: SubscriptionProductType) => {
    return <SubscriptionButton loading={managementLoading} product={product} getManagement={handleGotoManageStripe} />;
  };

  const handlePeriodChange = useCallback((newPeriod: PeriodType | null) => {
    setPeriod(newPeriod);
  }, []);

  useEffect(() => {
    if (subscriptionInitialPeriod) {
      setPeriod(subscriptionInitialPeriod);
    }
  }, [subscriptionInitialPeriod]);

  useEffect(() => {
    if (!intent || !intent.productId || !(userInfo && isUserInfoInitialized)) {
      return;
    }

    let fullProduct: SubscriptionProductType | undefined;
    for (const productPlanList of Object.values(subscriptionProducts || {})) {
      for (const product of Object.values(productPlanList)) {
        if (product && product.id === intent.productId) {
          fullProduct = product as SubscriptionProductType;
        }
      }
    }

    if (fullProduct) {
      let errorMessage = '';
      // 当前订阅
      if (fullProduct.product_pay_status === 'current') {
        errorMessage = t('payments.plans.currentSupport');
      } else if (fullProduct.product_pay_status === 'unavailable') {
        // 不可购买
        if (nextPlan?.next_plan_type) {
          errorMessage = t('payments.plans.nextPlanSupport');
        }
        // 非免费套餐, 支付宝渠道
        if (fullProduct.pay_channel === 'alipay') {
          // 当前是订阅套餐，无法按月/按年购买
          if (subscription?.product_type === 'subscription' && fullProduct?.product_type === 'pass') {
            errorMessage = `您已订阅，无法按${fullProduct.billing_period === 'monthly' ? '月' : '年'}购买`;
          }
          // 当前订阅的是免费套餐，无法按月/按年购买
          if (subscription?.product_type === 'pass' && fullProduct?.product_type === 'subscription') {
            errorMessage = `您已按${subscription.billing_period === 'monthly' ? '月' : '年'}购买，无法订阅`;
          }
          errorMessage = '不支持降级';
        }
      }

      if (errorMessage) {
        toast({
          type: 'warning',
          message: errorMessage,
        });
      } else {
        pay(fullProduct);
      }
    }
    clearPaymentIntent();
  }, [intent, isUserInfoInitialized, subscriptionProducts, subscription, nextPlan, pay, clearPaymentIntent]);

  return (
    <div className="flex w-full max-w-[1364px] select-none flex-col justify-center">
      <PlanDetail
        period={period}
        setPeriod={handlePeriodChange}
        loadingProducts={loadingProducts}
        error={error}
        products={subscriptionProducts}
        tabList={subscriptionTabList}
      />
      <ComparisonTable
        products={subscriptionProducts}
        tabList={subscriptionTabList}
        renderSubscriptionButton={renderSubscriptionButton}
        period={period}
        setPeriod={handlePeriodChange}
      />
    </div>
  );
};

export const Pricing: FC = () => {
  return <PricingContent />;
};
